import api from './api';
import { API_ROUTES } from '../config/api';
import {
  OptimizationRun,
  OptimizationRunParams,
  OptimizationResults,
  OptimizationRunResult,
  PredictionModel,
  ArticleAggregatedOrder,
  OptimizedContainer,
} from '../types/optimization';

export class OptimizationService {
  /**
   * Run optimization for a database
   */
  static async runOptimization(
    databaseId: string,
    params: OptimizationRunParams
  ): Promise<OptimizationRunResult> {
    const response = await api.post(
      API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.RUN(databaseId),
      params
    );
    return response.data;
  }

  /**
   * Get optimization results
   */
  static async getOptimizationResults(
    databaseId: string,
    optimizationRunId: string
  ): Promise<OptimizationResults> {
    const response = await api.get(
      API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.RESULTS(databaseId, optimizationRunId)
    );
    return response.data;
  }

  /**
   * List all optimization runs for a database
   */
  static async listOptimizationRuns(
    databaseId: string
  ): Promise<OptimizationRun[]> {
    const response = await api.get(API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.LIST(databaseId));
    return response.data;
  }

  /**
   * Set status for a single optimized order line
   */
  static async setOrderStatus(
    databaseId: string,
    optimizedOrderId: string,
    payload: { status: 'pending' | 'ordered' | 'delivered' | 'cancelled'; order_date?: string; expected_delivery_date?: string; actual_delivery_date?: string; quantity_ordered?: number; force?: boolean }
  ) {
    const response = await api.post(
      API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.SET_ORDER_STATUS(databaseId, optimizedOrderId),
      payload
    );
    return response.data;
  }

  /**
   * Set status for all orders in a container
   */
  static async setContainerStatus(
    databaseId: string,
    containerId: string,
    payload: { status: 'pending' | 'ordered' | 'delivered' | 'cancelled'; order_date?: string; expected_delivery_date?: string; actual_delivery_date?: string; quantity_ordered?: number; force?: boolean }
  ) {
    const response = await api.post(
      API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.SET_CONTAINER_STATUS(databaseId, containerId),
      payload
    );
    return response.data;
  }

  /**
   * Set status for all orders in a run
   */
  static async setRunStatus(
    databaseId: string,
    runId: string,
    payload: { status: 'pending' | 'ordered' | 'delivered' | 'cancelled'; order_date?: string; expected_delivery_date?: string; actual_delivery_date?: string; quantity_ordered?: number; force?: boolean }
  ) {
    const response = await api.post(
      API_ROUTES.OPTIMIZATION.BASE + API_ROUTES.OPTIMIZATION.SET_RUN_STATUS(databaseId, runId),
      payload
    );
    return response.data;
  }

  /**
   * Get available prediction models for a database
   */
  static async getAvailableModels(
    databaseId: string
  ): Promise<PredictionModel[]> {
    const response = await api.get(
      API_ROUTES.PREDICTION.BASE + API_ROUTES.PREDICTION.AVAILABLE_MODELS(databaseId)
    );
    return response.data;
  }

  /**
   * Get coverage data for articles
   */
  static async getCoverageData(
    databaseId: string,
    params: {
      target_month: string;
      model_name?: string;
      article_refs?: string[];
    }
  ) {
    const response = await api.post(
      API_ROUTES.STOCK.BASE + API_ROUTES.STOCK.COVERAGE(databaseId),
      params
    );
    return response.data;
  }

  /**
   * Calculate summary statistics for optimization results
   */
  static calculateSummary(results: OptimizationResults) {
    const { containers } = results;
    
    if (!containers || containers.length === 0) {
      return {
        total_containers: 0,
        total_volume_m3: 0,
        average_fill_rate: 0,
        location_groups: [],
        containers_by_type: { '30m3': 0, '65m3': 0 },
        total_orders: 0,
        estimated_delivery_range: { min_months: 0, max_months: 0 }
      };
    }

    const locationGroups = Array.from(
      new Set(containers.map(c => c.location_group))
    );
    
    const containersByType = containers.reduce(
      (acc, container) => {
        acc[container.container_type]++;
        return acc;
      },
      { '30m3': 0, '65m3': 0 }
    );

    const totalVolume = containers.reduce(
      (sum, container) => sum + container.total_volume_m3,
      0
    );

    const averageFillRate = containers.reduce(
      (sum, container) => sum + container.fill_rate,
      0
    ) / containers.length;

    const totalOrders = containers.reduce(
      (sum, container) => sum + container.orders.length,
      0
    );

    const deliveryDelays = containers.map(c => c.max_delivery_delay_months);
    const estimatedDeliveryRange = {
      min_months: Math.min(...deliveryDelays),
      max_months: Math.max(...deliveryDelays)
    };

    return {
      total_containers: containers.length,
      total_volume_m3: totalVolume,
      average_fill_rate: averageFillRate,
      location_groups: locationGroups,
      containers_by_type: containersByType,
      total_orders: totalOrders,
      estimated_delivery_range: estimatedDeliveryRange
    };
  }

  /**
   * Build a stable display index 1..n for each container id (global ordering)
   */
  static buildContainerDisplayIndex(containers: OptimizedContainer[]): Record<string, number> {
    // Sort by location then type for stability
    const sorted = [...(containers || [])].sort((a, b) => {
      const aLoc = String(a.location_group || '');
      const bLoc = String(b.location_group || '');
      if (aLoc !== bLoc) return aLoc.localeCompare(bLoc);
      const aType = String(a.container_type || '');
      const bType = String(b.container_type || '');
      if (aType !== bType) return aType.localeCompare(bType);
      return String(a.id).localeCompare(String(b.id));
    });
    const map: Record<string, number> = {};
    sorted.forEach((c, idx) => { map[String(c.id)] = idx + 1; });
    return map;
  }

  /**
   * Aggregate per-article orders with container split
   */
  static aggregateByArticle(results: OptimizationResults): ArticleAggregatedOrder[] {
    const { containers } = results;
    const displayIndex = this.buildContainerDisplayIndex(containers || []);
    const byKey: Record<string, ArticleAggregatedOrder> = {};

    const pushSplit = (
      agg: ArticleAggregatedOrder,
      container: OptimizedContainer,
      quantity: number,
      volume_m3: number,
      projected_coverage_delta?: number,
      current_coverage?: number
    ) => {
      agg.containers_split.push({
        container_id: String(container.id),
        location_group: container.location_group,
        container_type: container.container_type,
        quantity,
        volume_m3,
        display_index: displayIndex[String(container.id)] || 0,
      });
      // Update aggregated coverages using simple means
      if (typeof projected_coverage_delta === 'number') {
        // aggregate projected coverage by taking the max per article across containers for simplicity
        // We can refine later to a weighted approach if needed
        const projected = (agg.current_coverage || 0) + projected_coverage_delta;
        if (!isNaN(projected)) {
          agg.projected_coverage = Math.max(agg.projected_coverage || 0, projected);
        }
      }
      if (typeof current_coverage === 'number' && agg.current_coverage === undefined) {
        agg.current_coverage = current_coverage;
        // Initialize projected with current if not yet
        if (agg.projected_coverage === undefined) {
          agg.projected_coverage = current_coverage;
        }
      }
    };

    for (const container of containers || []) {
      for (const order of container.orders) {
        const key = `${order.supplier_name}::${order.article_ref}`;
        if (!byKey[key]) {
          byKey[key] = {
            article_ref: order.article_ref,
            supplier_name: order.supplier_name,
            total_quantity: 0,
            total_volume_m3: 0,
            projected_coverage: order.projected_coverage, // seed
            current_coverage: order.current_coverage,
            moq: order.moq,
            containers_split: [],
          };
        }
        byKey[key].total_quantity += order.quantity_to_order;
        byKey[key].total_volume_m3 += order.volume_m3;
        // propagate MOQ (use max if multiple suppliers/definitions)
        if (typeof order.moq === 'number') {
          const existing = byKey[key].moq ?? 0;
          byKey[key].moq = Math.max(existing, order.moq);
        }
        const delta = order.projected_coverage - order.current_coverage;
        pushSplit(byKey[key], container, order.quantity_to_order, order.volume_m3, delta, order.current_coverage);
        // Also update projected as max across any container entries for this article
        byKey[key].projected_coverage = Math.max(byKey[key].projected_coverage || 0, order.projected_coverage);
      }
    }

    return Object.values(byKey).sort((a, b) => a.article_ref.localeCompare(b.article_ref));
  }

  /**
   * Format optimization status for display
   */
  static formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      pending: 'En attente',
      running: 'En cours...',
      completed: 'Terminé',
      failed: 'Échoué'
    };
    return statusMap[status] || status;
  }

  /**
   * Format container type for display
   */
  static formatContainerType(type: '30m3' | '65m3'): string {
    return `Container ${type}`;
  }

  /**
   * Format coverage values for display
   */
  static formatCoverage(coverage: number): string {
    return `${coverage.toFixed(1)} mois`;
  }

  /**
   * Format volume for display
   */
  static formatVolume(volume: number): string {
    return `${volume.toFixed(2)} m³`;
  }

  /**
   * Calculate fill rate percentage
   */
  static formatFillRate(fillRate: number): string {
    return `${(fillRate * 100).toFixed(1)}%`;
  }
}

export default OptimizationService;
